import { DrupalNodeExternalResource} from "@/types"
import { PageInterface } from "./PageInterface"
import { AcquianFeedback } from "../block/AcquianFeedback.client"
import { ContentHelp } from "../block/ContentHelp.client"
import { ExternalResourceInterface, LoadExternalResource } from "@/lib/drupal/external-resource"
import { ReactNode, Suspense } from "react"
import { DocumentSidebar, DocumentSidebarLoading } from "../block/document-sidebar"
import { TocSidebar } from "../block/TocSidebar.client"
import { MetadataRoute } from "next"

/**
 * Base class for external resources.
 * 
 * Use this class to extend renderers for external resources.
 * @see AcquiaCliResource (lib/acli/backend.tsx) for an example.
 */
export abstract class ExternalResourceBase<T extends {} = {}> implements ExternalResourceInterface<T> {
  abstract useDocumentSidebar: boolean;
  abstract useTableOfContents: boolean;
  abstract getPageTitle(slug: string): Promise<string>

  async getHeaderChildren(slug: string): Promise<JSX.Element> {
    return <></>
  }

  async getContext(): Promise<T> {
    return {} as T
  }

  async getSidebar(context: PageInterface<DrupalNodeExternalResource>): Promise<JSX.Element> {
    return <></>
  }
  
  abstract getPageContent(context: PageInterface<DrupalNodeExternalResource>): Promise<JSX.Element>
  abstract buildSitemap(baseUrl: string): Promise<MetadataRoute.Sitemap>
}

export const ExternalResource = (context: PageInterface<DrupalNodeExternalResource>) => {
  const externalResource = LoadExternalResource(context.resource)
  return <>
    { externalResource.useDocumentSidebar && (
      <Suspense fallback={<DocumentSidebarLoading />}>
        <DocumentSidebar resource={context.resource} product={context.resource.field_product} path={context.path} />
      </Suspense>
    )}
    <TocSidebarRenderer withSidebar={externalResource.useTableOfContents} sidebar={externalResource.getSidebar(context)}>
        {externalResource.getPageContent(context)}
        <AcquianFeedback resource={context.resource} />
        <ContentHelp resource={context.resource} />
    </TocSidebarRenderer>
  </>
}

const TocSidebarRenderer = ({ withSidebar, children, sidebar}: {
  withSidebar: boolean
  children: ReactNode
  sidebar?: ReactNode
}) => {
  return withSidebar ? <TocSidebar main={children}>{sidebar}</TocSidebar> : <>{children}</>
}